import type { PresignedPost } from "@aws-sdk/s3-presigned-post";
import type { ICer, ICerFlat } from "@bbj-types/candidate/cer";
import type { ObjectId } from "@bbj-types/utils";
import { useCallback } from "react";
import { axiosV2 } from "./axios";

export const usePostCandidateCer = () => {
  const [state, execute, cancel] = axiosV2<
    { index: number; cer: ICer[] },
    { cer: ICerFlat; deleteCerId?: ObjectId },
    unknown
  >(
    {
      url: "/candidates/:id/cer",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (candidateId: string, cer: ICerFlat, deleteCerId?: ObjectId) => {
      return execute({
        url: `/candidates/${candidateId}/cer`,
        data: { cer, deleteCerId },
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const usePreviewCandidateCer = () => {
  const [state, execute, cancel] = axiosV2<string, ICerFlat, unknown>(
    {
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (cer: ICerFlat) => {
      return execute({
        url: "/candidates/cerPreview",
        data: cer,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export enum Notification {
  AUTHOR = "author",
  MANAGER = "manager",
  BENEFICIARY = "beneficiary",
}

export enum CERSignatureType {
  PAPER = "paper",
  EMAIL = "email",
  COMPUTER = "computer",
  COMPUTER_SMS = "computerSms",
}

export interface ICerSign {
  notifications: Readonly<Record<Notification, boolean>>;
  otpSms: Readonly<boolean>;
  type: CERSignatureType.COMPUTER | CERSignatureType.COMPUTER_SMS | CERSignatureType.EMAIL;
}

export interface ISigner {
  id: string;
  signature_link: string;
}

export interface ICandidateSignature {
  signers: [ISigner, ISigner, ISigner | undefined];
}

export const useSignCandidateCer = () => {
  const [state, execute, cancel] = axiosV2<ICandidateSignature, ICerSign, unknown>(
    {
      url: "/candidates/:id/cer/:cerId/sign",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (candidateId: string, cerId: string, options: ICerSign) => {
      return execute({
        url: `/candidates/${candidateId}/cer/${cerId}/sign`,
        data: options,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useDeleteCandidateCer = () => {
  const [state, execute, cancel] = axiosV2<void, void, unknown>(
    {
      url: "/candidates/:id/cer/:cerId",
      method: "DELETE",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (candidateId: string, cerId: string) => {
      return execute({
        url: `/candidates/${candidateId}/cer/${cerId}`,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useCerPreparePaperSignature = () => {
  const [state, execute, cancel] = axiosV2<PresignedPost, void, unknown>(
    {
      url: "/candidates/:id/cer/:cerId/signPaper",
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (candidateId: string, cerId: string) => {
      return execute({
        url: `/candidates/${candidateId}/cer/${cerId}/signPaper`,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useCerValidatePaperSignature = () => {
  const [state, execute, cancel] = axiosV2<void, void, unknown>(
    {
      url: "/candidates/:id/cer/:cerId/signPaper",
      method: "PATCH",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (candidateId: string, cerId: string) => {
      return execute({
        url: `/candidates/${candidateId}/cer/${cerId}/signPaper`,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useCerRetryNeolink = () => {
  const [state, execute, cancel] = axiosV2<void, void, unknown>(
    {
      url: "/candidates/:id/cer/:cerId/neolink/retry",
      method: "PATCH",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (candidateId: string, cerId: string) => {
      return execute({
        url: `/candidates/${candidateId}/cer/${cerId}/neolink/retry`,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};
